import { Scope } from '@developerz/domain';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { ApiError } from '../lib/api';
import { createMutation } from '../lib/mutation';
import {
  decidePending,
  type RepoPrRow,
  safeDecode,
  usePr,
  useRepoPendingPrs,
} from '../lib/repo-detail';
import { useScopeVerdict } from '../lib/scope-gate';
import { LoadMore } from './LoadMore';
import { Panel } from './Panel';
import { PendingTable } from './PendingTable';
import { PrPendingDrawer, type PrStaleness } from './PrPendingDrawer';
import { QueryBoundary } from './QueryBoundary';
import type { RepoTabProps } from './RepoOverviewTab';
import { Skeleton } from './Skeleton';

// Repo detail → Pending (#195): the ask_before_acting queue. PRs whose merge
// decision is parked on `ask` wait here for an explicit approve/skip; the
// drawer shows the drafted action's context before deciding.
//
// Container. The list feeds the table; the DRAWER is fed by a point read of the
// one PR it was opened on (`GET /v1/prs/:id`, #1036) — re-read after a decision,
// so the panel shows what the server stored rather than the row the list last
// fetched. The opening row stands in until that lands, so nothing flashes empty.
//
// THE SERVER FILTERS AND PAGES (plan 101 slice 17 row 17-2). The list is
// `useRepoPendingPrs`: open PRs stamped `ask`, cut by the API and paged by its
// cursor. A settled PR keeps its stamp, so filtering page one of every PR here
// rendered merged rows with live Approve / Skip and never reached an older ask.
// A decision `reload()`s from page one: the decided row leaves the filter, so a
// held deep cursor would read a shifted slice.

export function RepoPendingTab(props: RepoTabProps): JSX.Element {
  const prs = useRepoPendingPrs(() => props.encoded, props.token);
  // Both decision routes are `write:prs` (#7426: a member's Approve was a 403).
  const mayDecide = useScopeVerdict(Scope.WritePrs, props.token);
  const [drawerFor, setDrawerFor] = createSignal<RepoPrRow | null>(null);

  const detail = usePr(() => drawerFor()?.id ?? null, props.token);
  const drawerRow = (): RepoPrRow | null =>
    drawerFor() === null ? null : (detail.data ?? drawerFor());
  // A failed re-read leaves the opening row on screen — usable, but labelled.
  const staleness = (): PrStaleness => {
    if (!detail.isError) return null;
    return detail.error instanceof ApiError && detail.error.status === 404 ? 'gone' : 'stale';
  };

  const repoPr = (p: RepoPrRow): string => `${safeDecode(props.encoded)}#${p.github_number}`;

  const decision = createMutation({
    call: (prId: string, verdict: 'approve' | 'skip', headSha: string) =>
      decidePending(prId, verdict, headSha, props.token),
    // Surface it — a destructive-adjacent action must never silently no-op.
    // Read the way Home and `/pending` read it: the refusal is the API's own
    // `detail` (`apps/dashboard/CLAUDE.md`, "the server is the arbiter"); a head
    // that moved under the human (plan 101 M2) reads `HeadRefusal`'s localized
    // sentence; this fixed line only when the failure carried no detail.
    fallback: t('repo_detail.pending_decide_error'),
    onSettled: async () => {
      // Server truth either way — a 409 (raced decision) also resolves here.
      // The open PR is re-read on its own: the list answers the table, never
      // the drawer, which would otherwise repaint the pre-decision row.
      prs.reload();
      if (drawerFor() !== null) await detail.refetch();
    },
  });
  const busy = decision.pending;
  const decide = (prId: string, verdict: 'approve' | 'skip', headSha: string): void => {
    if (mayDecide() === 'allowed') void decision.run(prId, verdict, headSha);
  };

  return (
    <div class="repo-detail__stack">
      <Show when={decision.error()}>
        {(message) => (
          <div class="alert alert--destructive" role="alert">
            <p class="alert__title">{message()}</p>
          </div>
        )}
      </Show>
      <Panel title={t('repo_detail.pending_title')}>
        <QueryBoundary
          query={prs.view}
          isEmpty={(rows) => rows.length === 0}
          loading={<Skeleton height="10rem" />}
          empty={<div class="list-empty">{t('repo_detail.pending_empty')}</div>}
        >
          {(rows) => (
            <>
              <PendingTable
                rows={rows.map((p) => ({
                  id: p.id,
                  repo_pr: repoPr(p),
                  drafted_action: t('repo_detail.pending_action_merge', { ci: p.ci_status }),
                  subject: p.title ?? '',
                  author: p.author_login ?? null,
                  head_sha: p.head_sha,
                }))}
                busy={busy()}
                mayDecide={mayDecide()}
                onApprove={(id, head) => decide(id, 'approve', head)}
                onSkip={(id, head) => decide(id, 'skip', head)}
                onEdit={(id) => setDrawerFor(rows.find((p) => p.id === id) ?? null)}
              />
              <LoadMore list={prs} />
            </>
          )}
        </QueryBoundary>
      </Panel>

      <Show when={drawerRow()}>
        {(row) => (
          <PrPendingDrawer
            pr={row()}
            title={repoPr(row())}
            token={props.token}
            staleness={staleness()}
            onClose={() => setDrawerFor(null)}
          />
        )}
      </Show>
    </div>
  );
}
